import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { capitalize, pickBy } from 'lodash'
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { CloseOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useUnit } from 'effector-react'
import {
  $countriesList,
  dictionariesMounted,
} from 'src/features/dictionaries/model'
import {
  FREQUENCY_OPTIONS,
  iconButtonStyle,
  POSITIVE_DECIMAL_MESSAGE,
  POSITIVE_DECIMAL_PATTERN,
  PRODUCT_OPTIONS,
  REQUIRED_FIELD,
  STATUS_MAP_COLOR,
  STATUS_MAP_NAME,
  STATUS_PERIOD,
  STATUS_PERIOD_COLOR,
  STATUS_PERIOD_LABELS,
} from 'src/pages/budget/const'
import { path } from 'src/libs/path'
import { filterOption, isEqualObject } from 'src/pages/budget/utils'
import { ModalArticleItem, UiText } from 'src/pages/budget/components'
import { openModalArticleItem } from 'src/pages/budget/model'
import {
  useContextRequest,
  useCurrencies,
  useCurrencyGate,
} from 'src/pages/budget/budgetRequests/hooks'
import { STATUS } from 'src/pages/budget/const'
import { TStatus } from 'src/pages/budget/types'
import {
  createRequestFx,
  openModalRequestsApprove,
  openModalRequestsReject,
  returnToDraftRequestFx,
  submitRequestFx,
  updateRequestFx,
} from 'src/pages/budget/budgetRequests/model'
import {
  BudgetRequestCostByCountry,
  RequestPayload,
} from 'src/pages/budget/budgetRequests/types'
import {
  formatUsdAmount,
  getUsdEquivalent,
  getUsdEstimate,
  normalizeDecimalInput,
  sumUsdEstimates,
} from 'src/pages/budget/budgetRequests/currency'

import dayjs from 'dayjs'
import { getRequestFieldDisabled, RequestFormMode } from './fieldAccess'
dayjs.locale('ru')

type FormRequestProps = {
  initialValues: Partial<RequestPayload>
  mode: RequestFormMode
  isCeo?: boolean
}

export const FormRequest = ({
  initialValues,
  mode,
  isCeo,
}: FormRequestProps) => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingStatus, setLoadingStatus] = useState<TStatus | null>(null)
  const [isFrequency, setIsFrequency] = useState(() =>
    Boolean(initialValues.autoPaymentFrequency),
  )
  const { accessibleArticles, teamsOptions, budgetPeriodList } =
    useContextRequest()
  useCurrencyGate()
  const currencies = useCurrencies()
  const currencyOptions = currencies.map(({ code }) => ({
    value: code,
    label: code,
  }))
  const [form] = Form.useForm()
  const countriesList = useUnit($countriesList)

  useEffect(() => {
    dictionariesMounted()
  }, [])

  const { Title, Text } = Typography
  const { Item } = Form

  const isNew = mode === 'createOwn'
  const isEditOwn = mode === 'editOwn'
  const isEditTeamRequest = mode === 'editForApprove'

  const disabled = getRequestFieldDisabled({
    mode,
    status: initialValues.status,
    isCeo,
    hasApprovalSteps: initialValues.availableApprovalSteps
      ? initialValues.availableApprovalSteps.length > 0
      : false,
    initialIsEarlyPayment: initialValues.isEarlyPayment,
  })
  const WIDTH = 340
  const GAP = 8

  const currentArticleId = Form.useWatch('article', form)
  const autoPaymentEnabled = Form.useWatch('autoPaymentEnabled', form)
  const currentBudgetPeriodId = Form.useWatch('budgetPeriod', form)
  const amount = Form.useWatch('amount', form)
  const currency = Form.useWatch('currency', form)
  const costByCountryEnabled = Form.useWatch('costByCountryEnabled', form)
  const costByCountry: BudgetRequestCostByCountry[] =
    Form.useWatch('costByCountry', form) ?? []
  const usdEquivalent = getUsdEquivalent(
    amount ?? '',
    currency ?? '',
    currencies,
  )
  const preliminaryUsdTotal = sumUsdEstimates(
    costByCountry.map((item) =>
      getUsdEstimate(item?.amount ?? '', item?.currency ?? '', currencies),
    ),
  )
  const countryOptions = useMemo(
    () => Object.values(countriesList?.countries ?? {}).map((country) => ({
      value: country.A3,
      label: `${country.name} (${country.A3})`,
    })),
    [countriesList],
  )

  const budgetPeriodOptions = budgetPeriodList
    .filter((item) => item.status !== STATUS_PERIOD.CLOSED)
    .map((item) => ({
      label: capitalize(
        dayjs(item.periodFormatted, 'YYYY-MM').format('MMMM YYYY'),
      ),
      value: item.id,
    }))

  const optionsArticle = accessibleArticles.map(({ id, number, name }) => ({
    value: id,
    label: `${number}. ${name}`,
  }))

  const optionsArticleItems = currentArticleId
    ? accessibleArticles
        .find(({ id }) => id === currentArticleId)
        ?.items.map(({ id, fullNumber, name }) => ({
          value: id,
          label: `${fullNumber}. ${name}`,
        }))
    : []

  const getStatus = () => {
    const status = budgetPeriodList.find(
      (item) => item.id === currentBudgetPeriodId,
    )?.status

    return status
      ? {
          label: STATUS_PERIOD_LABELS[status],
          color: STATUS_PERIOD_COLOR[status],
        }
      : { label: 'Статус неизвестен', color: 'default' }
  }

  const initRequestStatus = initialValues.status

  const compareAndSave = async (body: Partial<RequestPayload>) => {
    const changedFields: Partial<RequestPayload> = pickBy(
      body,
      (value, key) => !isEqualObject({ [key]: value }, initialValues),
    )

    if (Object.keys(changedFields).length === 0) return

    await updateRequestFx({
      body: changedFields,
      url: `/budget/budget_request/${id}`,
    })
  }

  const handleSubmit = async (status: TStatus) => {
    setLoadingStatus(status)
    try {
      await form.validateFields()
      const payload = form.getFieldsValue(true)
      const body = {
        team: payload.team,
        article: payload.article,
        articleItem: payload.articleItem,
        isEarlyPayment: initialValues.isEarlyPayment ?? false,
        budgetPeriod: payload.budgetPeriod,
        description: payload.description,
        ...(payload.costByCountryEnabled
          ? {
              costByCountryEnabled: true,
              costByCountry: (
                (payload.costByCountry ?? []) as BudgetRequestCostByCountry[]
              ).map((item) => ({
                ...item,
                amount: normalizeDecimalInput(item.amount),
              })),
            }
          : {
              costByCountryEnabled: false,
              amount: normalizeDecimalInput(payload.amount),
              currency: payload.currency,
            }),
        autoPaymentEnabled: isFrequency ? false : payload.autoPaymentEnabled,
        autoPaymentFrequency: payload.autoPaymentEnabled
          ? null
          : payload.autoPaymentFrequency ?? null,

        ...(payload.productIds && {
          productIds: payload.productIds,
        }),

        ...(payload.comment && {
          comment: payload.comment,
        }),
      }

      if (isNew) {
        await createRequestFx({ body: { ...body, status } })
      } else {
        await compareAndSave(body)
      }

      if (isEditOwn) {
        if (status !== initialValues.status) {
          if (status === STATUS.DRAFT) {
            await returnToDraftRequestFx({
              url: `/budget/budget_request/${id}/return_to_draft`,
            })
          }

          if (status === STATUS.SUBMITTED) {
            await submitRequestFx({
              url: `/budget/budget_request/${id}/submit`,
            })
          }
        }
      }

      return true
    } catch (error) {
      console.log('validation error:', error)
      return false
    } finally {
      setLoadingStatus(null)
    }
  }

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        autoComplete="off"
        style={{
          padding: '24px 22px',
          backgroundColor: 'white',
          borderRadius: 16,
          minWidth: 390,
        }}
      >
        <Flex justify="space-between">
          <Title level={3} style={{ marginBottom: GAP }}>
            Общая информация{' '}
          </Title>
          <Text style={{ fontSize: 14 }}>
            {id ? `Заявка №${id}` : ''}{' '}
            {initRequestStatus && (
              <Tag color={STATUS_MAP_COLOR[initRequestStatus] ?? ''}>
                {STATUS_MAP_NAME[initRequestStatus]}
              </Tag>
            )}
          </Text>
        </Flex>

        <Flex gap={GAP}>
          <Item
            label={
              <UiText size={18} type="secondary">
                Месяц бюджета *
              </UiText>
            }
            name="budgetPeriod"
            style={{ width: WIDTH }}
          >
            <Select
              placeholder="Выберите месяц"
              disabled={disabled.budgetPeriod}
              options={budgetPeriodOptions}
              optionRender={(option) => {
                const isActive = option.value === initialValues?.budgetPeriod

                return (
                  <div
                    style={{
                      color: isActive ? 'var(--color-primary)' : undefined,
                      fontSize: isActive ? 16 : 14,
                    }}
                  >
                    {option.label}
                  </div>
                )
              }}
            />
          </Item>
          <Space style={{ marginTop: 10 }}>
            <UiText size={18} type="secondary">
              Статус:
            </UiText>
            <Tag
              style={{ fontSize: 14, padding: '3px 6px' }}
              color={getStatus().color}
            >
              {getStatus().label}
            </Tag>
          </Space>
        </Flex>
        <Flex gap={GAP} wrap>
          <Flex gap={GAP} wrap>
            <Item
              label={
                <UiText size={18} type="secondary">
                  Статья *
                </UiText>
              }
              name="article"
              style={{ width: WIDTH }}
              required={false}
              rules={[{ required: true, message: REQUIRED_FIELD }]}
            >
              <Select
                placeholder="Выберите статью"
                options={optionsArticle}
                allowClear
                disabled={disabled.article}
                onChange={() => form.setFieldValue('articleItem', null)}
                showSearch
                filterOption={filterOption}
              />
            </Item>

            <Item
              label={
                <UiText size={18} type="secondary">
                  Пункт *
                </UiText>
              }
              name="articleItem"
              style={{ width: WIDTH }}
              required={false}
              rules={[{ required: true, message: REQUIRED_FIELD }]}
            >
              <Select
                placeholder="Выберите пункт статьи"
                disabled={disabled.articleItem}
                showSearch
                filterOption={filterOption}
                allowClear
                options={optionsArticleItems}
                popupRender={(menu) => (
                  <>
                    {currentArticleId && (
                      <>
                        <Button
                          type="text"
                          style={{
                            marginTop: 5,
                            width: '100%',
                          }}
                          onClick={() => {
                            const currentArticle = accessibleArticles.find(
                              ({ id }) => id === currentArticleId,
                            )
                            if (!currentArticle) return
                            const { id, name, number } = currentArticle

                            openModalArticleItem({
                              article: {
                                articleId: id,
                                number,
                                name,
                                description: '',
                                users: [],
                                items: [],
                              },
                              mode: 'new',
                              articleItem: null,
                            })
                          }}
                        >
                          <Text
                            style={{
                              color: 'var(--color-primary)',
                              marginRight: 'auto',
                            }}
                          >
                            <PlusOutlined
                              style={{
                                ...iconButtonStyle,
                                color: 'var(--color-primary)',
                              }}
                            />{' '}
                            Создать новый пункт
                          </Text>
                        </Button>
                        <Divider style={{ margin: '8px 0' }} />
                      </>
                    )}
                    {menu}
                  </>
                )}
              />
            </Item>
          </Flex>

          <Flex gap={GAP} wrap>
            <Item
              label={
                <UiText size={18} type="secondary">
                  Команда *
                </UiText>
              }
              name="team"
              style={{ width: WIDTH }}
              required={false}
              rules={[{ required: true, message: REQUIRED_FIELD }]}
            >
              <Select
                showSearch
                styles={{
                  popup: {
                    root: { maxHeight: 400, overflow: 'auto' },
                  },
                }}
                disabled={disabled.team}
                placeholder="Выберите команду"
                options={teamsOptions}
              />
            </Item>

            <Item
              label={
                <UiText size={18} type="secondary">
                  Продукт (необязательно)
                </UiText>
              }
              name="productIds"
              style={{ width: WIDTH }}
            >
              <Select
                mode="multiple"
                placeholder="Один или несколько продуктов"
                options={PRODUCT_OPTIONS}
                disabled={disabled.productIds}
              />
            </Item>
          </Flex>
        </Flex>

        <Item
          label={
            <UiText size={18} type="secondary">
              Описание *
            </UiText>
          }
          name="description"
          required={false}
          rules={[
            { required: true, message: REQUIRED_FIELD },
            {
              max: 1000,
              message: 'Описание заявки не должно превышать 1000 символов',
            },
          ]}
        >
          <TextArea
            style={{ width: WIDTH * 2 + GAP }}
            disabled={disabled.description}
            rows={6}
            placeholder="Опишите заявку кратко и понятно: зачем нужны средства и из чего складывается сумма. При необходимости приложите ссылку на Google Таблицу."
          />
        </Item>
        {isEditTeamRequest && (
          <Flex vertical gap={GAP} style={{ marginBottom: GAP }}>
            <UiText size={18} type="secondary">
              Заявитель
            </UiText>
            <Text>{initialValues.createdBy?.fullname ?? ''}</Text>
          </Flex>
        )}

        <Title level={3} style={{ marginBottom: GAP }}>
          Стоимость
        </Title>

        <UiText
          size={18}
          type="secondary"
          style={{ display: 'block', maxWidth: 760, marginBottom: 24 }}
        >
          Вы можете внести единую сумму или разделить стоимость по странам с
          отдельной суммой и валютой для каждой.
        </UiText>

        <Item
          label={<Text style={{ fontSize: 16 }}>Разделять по странам</Text>}
          name="costByCountryEnabled"
          valuePropName="checked"
        >
          <Switch
            disabled={disabled.amount || disabled.currency}
            onChange={(enabled) => {
              if (
                enabled &&
                (form.getFieldValue('costByCountry') ?? []).length === 0
              ) {
                form.setFieldValue('costByCountry', [{ currency: 'USD' }])
              }
            }}
          />
        </Item>

        {!costByCountryEnabled && <>
        <Flex gap={GAP} style={{ width: WIDTH }}>
          <Item
            label={
              <UiText size={18} type="secondary">
                Сумма *
              </UiText>
            }
            name="amount"
            style={{ flex: 2 }}
            required={false}
            rules={[
              { required: true, message: REQUIRED_FIELD },
              {
                pattern: POSITIVE_DECIMAL_PATTERN,
                message: POSITIVE_DECIMAL_MESSAGE,
              },
            ]}
          >
            <Input disabled={disabled.amount} autoComplete="off" />
          </Item>

          <Item
            label={
              <UiText size={18} type="secondary">
                Валюта *
              </UiText>
            }
            name="currency"
            style={{ flex: 1 }}
            required={false}
            rules={[{ required: true, message: REQUIRED_FIELD }]}
          >
            <Select
              showSearch
              options={currencyOptions}
              disabled={disabled.currency}
            />
          </Item>
        </Flex>
        {usdEquivalent && (
          <UiText size={18} type="secondary" style={{ display: 'block' }}>
            ≈ {formatUsdAmount(usdEquivalent)} USD
          </UiText>
        )}
        </>}
        {costByCountryEnabled && (
          <>
            <Form.List name="costByCountry">
              {(fields, { add, remove }) => (
                <Flex vertical gap={24} style={{ maxWidth: 860 }}>
                  {fields.map((field) => {
                    const countryUsdEquivalent = getUsdEstimate(
                      costByCountry[field.name]?.amount ?? '',
                      costByCountry[field.name]?.currency ?? '',
                      currencies,
                    )

                    return (
                      <Flex key={field.key} gap={12} align="start">
                      <Item
                        {...field}
                        name={[field.name, 'countryCode']}
                        label={
                          <UiText size={18} type="secondary">
                            Страна
                          </UiText>
                        }
                        rules={[{ required: true, message: REQUIRED_FIELD }]}
                        style={{ width: 342, marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          placeholder="Выбери страну"
                          options={countryOptions}
                          disabled={disabled.amount}
                        />
                      </Item>
                      <Flex vertical style={{ width: 286 }}>
                        <Item
                          {...field}
                          name={[field.name, 'amount']}
                          label={
                            <UiText size={18} type="secondary">
                              Сумма
                            </UiText>
                          }
                          rules={[
                            { required: true, message: REQUIRED_FIELD },
                            {
                              pattern: POSITIVE_DECIMAL_PATTERN,
                              message: POSITIVE_DECIMAL_MESSAGE,
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            disabled={disabled.amount}
                            autoComplete="off"
                          />
                        </Item>
                        {countryUsdEquivalent && (
                          <UiText
                            size={14}
                            type="secondary"
                            style={{ marginTop: 8 }}
                          >
                            ≈{' '}
                            {formatUsdAmount(countryUsdEquivalent)}{' '}
                            USD
                          </UiText>
                        )}
                      </Flex>
                      <Item
                        {...field}
                        name={[field.name, 'currency']}
                        label={
                          <UiText size={18} type="secondary">
                            Валюта
                          </UiText>
                        }
                        rules={[{ required: true, message: REQUIRED_FIELD }]}
                        style={{ width: 156, marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          options={currencyOptions}
                          disabled={disabled.currency}
                        />
                      </Item>
                      <Button
                        type="text"
                        color="blue"
                        variant="text"
                        icon={<CloseOutlined />}
                        onClick={() => remove(field.name)}
                        disabled={disabled.amount || fields.length <= 1}
                        style={{ width: 32, height: 40, marginTop: 38 }}
                        aria-label="Удалить страну"
                      />
                      </Flex>
                    )
                  })}
                  {preliminaryUsdTotal && (
                    <Flex gap={48}>
                      <UiText size={18} strong>
                        Предварительный итог
                      </UiText>
                      <UiText size={18} strong>
                        {formatUsdAmount(preliminaryUsdTotal)} USD
                      </UiText>
                    </Flex>
                  )}
                  <Button
                    color="blue"
                    variant="outlined"
                    icon={<PlusOutlined />}
                    onClick={() => add()}
                    disabled={disabled.amount}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Добавить страну
                  </Button>
                </Flex>
              )}
            </Form.List>
          </>
        )}

        <Title level={3} style={{ marginTop: 32, marginBottom: GAP }}>
          Регулярность
        </Title>

        <Checkbox
          checked={isFrequency}
          disabled={disabled.autoPaymentFrequency || autoPaymentEnabled}
          onChange={(e) => {
            setIsFrequency(e.target.checked)
            if (e.target.checked) {
              form.setFieldValue('autoPaymentEnabled', false)
            } else {
              form.setFieldValue('autoPaymentFrequency', null)
            }
          }}
        >
          <UiText
            size={18}
            type="secondary"
            style={{ display: 'inline-flex', gap: GAP }}
          >
            Автоплатёж
            <Tooltip title="Заявка будет автоматически создаваться в бюджете каждого месяца. Её нужно согласовать, но подавать на оплату вручную не потребуется">
              <QuestionCircleOutlined />
            </Tooltip>
          </UiText>
        </Checkbox>
        {isFrequency && (
          <Item
            name="autoPaymentFrequency"
            required={false}
            rules={[{ required: true, message: REQUIRED_FIELD }]}
          >
            <Radio.Group
              options={FREQUENCY_OPTIONS}
              disabled={disabled.autoPaymentFrequency}
            />
          </Item>
        )}

        <Item name="autoPaymentEnabled" valuePropName="checked">
          <Checkbox
            disabled={disabled.autoPaymentEnabled || isFrequency}
            onChange={(e) => {
              if (e.target.checked) {
                setIsFrequency(false)
                form.setFieldValue('autoPaymentFrequency', null)
              }
            }}
          >
            <UiText
              size={18}
              type="secondary"
              style={{ display: 'inline-flex', gap: 6 }}
            >
              Автоматически создавать заявку ежемесячно
              <Tooltip title="Каждый месяц в бюджете будет появляться черновик заявки с теми же параметрами и описанием. Перед отправкой его можно отредактировать, а на согласование и оплату он подаётся как обычно.">
                <QuestionCircleOutlined />
              </Tooltip>
            </UiText>
          </Checkbox>
        </Item>
        {isEditTeamRequest && initialValues.comment && (
          <Item
            label={
              <UiText size={18} type="secondary">
                Комментарий
              </UiText>
            }
            name="comment"
          >
            <TextArea
              style={{ width: 688, backgroundColor: 'var(--color-red-10)' }}
              disabled={true}
              rows={2}
            />
          </Item>
        )}

        <Flex gap="middle" justify="flex-end" wrap>
          {isEditTeamRequest ? (
            <>
              <Button
                size="large"
                style={{ width: 300 }}
                variant="solid"
                color="red"
                loading={loadingStatus === STATUS.REJECTED}
                onClick={async () => {
                  const success = await handleSubmit(STATUS.REJECTED)
                  if (!success) return

                  form.resetFields()
                  navigate(path.budget.budgets.entry())
                  openModalRequestsReject({
                    items: [
                      {
                        id: Number(id),
                        description: initialValues.description ?? '',
                        availableApprovalSteps:
                          initialValues.availableApprovalSteps ?? [],
                      },
                    ],
                  })
                }}
              >
                Отклонить
              </Button>
              <Button
                size="large"
                style={{ width: 300 }}
                variant="solid"
                color="green"
                loading={loadingStatus === STATUS.APPROVED}
                onClick={async () => {
                  const success = await handleSubmit(STATUS.APPROVED)
                  if (!success) return
                  form.resetFields()
                  navigate(path.budget.budgets.entry())
                  openModalRequestsApprove({
                    items: [
                      {
                        id: Number(id),
                        description: initialValues.description ?? '',
                        availableApprovalSteps:
                          initialValues.availableApprovalSteps ?? [],
                      },
                    ],
                  })
                }}
              >
                Согласовать
              </Button>
            </>
          ) : (
            <>
              {(!isEditOwn || initialValues.status !== STATUS.DRAFT) && (
                <Button
                  size="large"
                  style={{ width: 300 }}
                  loading={loadingStatus === STATUS.DRAFT}
                  onClick={async () => {
                    const success = await handleSubmit(STATUS.DRAFT)
                    if (!success) return
                    form.resetFields()
                    navigate(path.budget.budgets.entry())
                  }}
                >
                  {isNew ? 'Создать черновик' : 'Вернуть в черновик'}
                </Button>
              )}
              <Button
                size="large"
                style={{ width: 300 }}
                color="cyan"
                variant="solid"
                loading={loadingStatus === 'submitted'}
                onClick={async () => {
                  const success = await handleSubmit(STATUS.SUBMITTED)
                  if (!success) return
                  form.resetFields()
                  navigate(path.budget.budgets.entry())
                }}
              >
                Отправить на согласование
              </Button>
            </>
          )}
        </Flex>
      </Form>
      <ModalArticleItem />
    </>
  )
}
